import { useRef, RefObject, VFC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Form } from 'antd';
import FroalaEditor, { MyComponentProps as DefaultFroalaProps } from 'react-froala-wysiwyg';

import { FroalaEditorLicenseKey, generateFileDomain } from 'constants/GlobalConfig';
import * as Actions from 'flex/Actions';
import { useAppSelector } from 'flex/utils';

// Require Editor JS files.
import 'froala-editor/js/froala_editor.pkgd.min.js';
import 'froala-editor/js/languages/ja.js';
import 'froala-editor/js/plugins/link.min.js';
import 'froala-editor/js/plugins/char_counter.min.js';
import 'froala-editor/js/plugins/image.min.js';


// Require Editor CSS files.
import 'froala-editor/css/froala_style.min.css';
import 'froala-editor/css/froala_editor.pkgd.min.css';
import 'froala-editor/css/themes/gray.min.css';
import 'froala-editor/css/plugins/image.min.css';

// Require Font Awesome.
import 'font-awesome/css/font-awesome.css';
import { FormInstance } from 'rc-field-form';

const MAX_SIZE = 50 * 1024 *1024;
const ERROR_UPLOAD = {
    BAD_LINK: {
        CODE: 1,
        MESSAGE: '対応していないファイルのため、読み込みできません。',
    },
    TEXT_LARGE: {
        CODE: 5,
        MESSAGE: 'ファイルサイズは50MB以下にしてください。',
    }
}

const defaultConfig = {
    attribution: false,
    charCounterMax: -1,
    codeBeautifierOptions: {
        indent_char: ' ',
        indent_size: 4,
        wrap_line_length: 0,
    },
    colorsBackground: [
        '#dc0000',
        '#004da0',
        '#f8b500',
        '#009d62',
        '#ec6c00',
        '#ffffff',
        '#314b57',
        'REMOVE',
    ],
    colorsHEXInput: false,
    colorsText: [
        '#dc0000',
        '#004da0',
        '#f8b500',
        '#009d62',
        '#ec6c00',
        '#ffffff',
        '#314b57',
        'REMOVE',
    ],
    events: {
        // コンポーネント中で上書きされるのでここには書かない。
        // ここに書く場合、コンポーネント内の処理をよしなに修正すること。
    },
    heightMin: 200,
    htmlAllowedTags: [
        'a',
        'br',
        'code',
        'em',
        'hr',
        'p',
        'span',
        'strong',
        'u',
        'img',
        'font',
    ],
    key: FroalaEditorLicenseKey,
    imageAllowedTypes: ['jpg', 'jpeg', 'png', 'gif', 'bmp'],
    imageEditButtons: [
        'imageReplace',
        'imageAlign',
        'imageCaption',
        'imageLink',
        'linkRemove',
        'imageRemove',
        'imageSize',
    ],
    imageInsertButtons: ['imageBack', 'imageUpload'],
    language: 'ja',
    linkAlwaysBlank: true,
    linkEditButtons: [
        'linkOpen',
        'linkEdit',
        'linkRemove',
    ],
    linkInsertButtons: ['linkBack'],
    placeholderText: '内容を入力してください',
    pluginsEnabled: [
        'align',
        'charCounter',
        'colors',
        'fullscreen',
        'image',
        'link',
        'lists',
        'print',
        'url',
    ],
    toolbarButtons: [
        'fullscreen',
        'bold',
        'italic',
        'underline',
        '|',
        'color',
        '|',
        'insertHR',
        'insertLink',
        'insertImage',
        'clearFormatting',
        '|',
        'help',
        '|',
        'undo',
        'redo',
        '|',
        'print',
    ],
    toolbarSticky: false,
    // .ant-modal-wrap の z-index が 1000 なので、それと同じ値にする。
    // そうしないと images の editButtons がモーダルの下に隠れる。
    zIndex: 1000,
};

type FroalaProps = {
    // object 型だと使い勝手が悪いので any とする
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    config?: any;
} & DefaultFroalaProps;

type Props = {
    /**
     * インラインファイルを保持する Form.Item の id
     */
    inlineFilesId?: string
    disabled: boolean
    /** antd 用 */
    // eslint-disable-next-line @typescript-eslint/ban-types
    value?: string | object
    /** antd 用 */
    // eslint-disable-next-line @typescript-eslint/ban-types
    onChange?: (value: string | object) => void
    inlineFilePrefix?: string
    /**
     * antd 用
     *
     * scrollToFirstError は id を基にしてスクロールするため、
     * 指定の id を設定する必要がある
     */
    id?: string
} & FroalaProps

/**
 * リッチテキストエディタ
 *
 * 文字数制限を指定する場合には、 Antd の Form.Item ないの rules で指定せず、
 * このコンポーネントの config で指定すること。
 *
 * 参考: #3792
 */
const RichTextEditor: VFC<Props> = ({
    config: _config = {},
    disabled,
    id = 'rich-text-editor',
    inlineFilesId = 'inlineFiles',
    model: _model,
    onChange,
    onModelChange: _onModelChange,
    value,
    ...restProps
}) => {
    // https://github.com/froala/react-froala-wysiwyg/issues/184#issuecomment-680194877
    const ref = useRef<FroalaEditor>(null);
    const editor = ref.current?.getEditor();
    const form = Form.useFormInstance();
    const [onUploadImage] = useImage(ref, form, inlineFilesId);
    const model = _model ?? value;
    const onModelChange = _onModelChange ?? onChange;
    const { events, ...restConfig } = _config;
    const HandleCharCounterUpdater = () => charCounterUpdater(ref, disabled);
    const HandleCommandsRedoUndo = () => handleCommandsRedoAndUndo(ref, form, inlineFilesId);
    const onPasteImage = (images: any) => handlePasteImage(images, ref);

    const config = {
        ...defaultConfig,
        events: {
            'charCounter.update': HandleCharCounterUpdater,
            initialized() {
                setTimeout(() => {//disabledの値によって使用可否を制御
                    disabled ? this.edit.off() : this.edit.on();
                });
            },
            'image.beforePasteUpload': onPasteImage,
            'image.beforeUpload': onUploadImage,
            'image.error': (error: any) => {
                const editor = ref.current?.getEditor();

                const layerImgUpload = editor.popups.get('image.insert').find('.fr-image-upload-layer');
                layerImgUpload.addClass('fr-pactive').removeClass('fr-active');

                const layerImgProgress = editor.popups.get('image.insert').find('.fr-image-progress-bar-layer');
                layerImgProgress.addClass('fr-pactive fr-active fr-error');
                layerImgProgress.find('h3').text(error?.message);
            },
            'image.removed': (img: any) => {
                const uuid = img[0].currentSrc.split('/').pop();
                const inlineFiles = form.getFieldValue(inlineFilesId) ?? [];
                form.setFieldsValue({
                    inlineFiles: inlineFiles.filter((v: string) => v !== uuid),
                });
            },
            'image.uploaded': () => false,
            'commands.undo': HandleCommandsRedoUndo,
            'commands.redo': HandleCommandsRedoUndo,
            ...events,
        },
        ...restConfig,
        scrollableContainer: `#${id}`,
    };

    useEffect(() => {// editorのdisabledを制御(disabledが渡されるタイミングが遅い場合こちらで制御する必要あり)
        if (!editor) return;
        if (disabled) {
            editor?.edit?.off();
        } else {
            editor?.edit?.on();
        }
    }, [disabled, editor]);

    return (
        <div id={id}>
            <FroalaEditor
                {...restProps}
                config={config}
                model={model}
                onModelChange={onModelChange}
                ref={ref}
            />
        </div>
    );
};

export default RichTextEditor;


/**
 * 文字数カウントが更新されたときの処理
 *
 * エディタが disabled の場合、文字数のカウントがされないため、
 * カウントが完了してから disabled にする。
 *
 * 参考: https://github.com/froala/wysiwyg-editor/issues/3501
 */
const charCounterUpdater = (ref: RefObject<FroalaEditor>, disabled: boolean) => () => {
    const editor = ref.current?.getEditor();
    // この Editor を使う属性は必須なので、文字数が 0 になることはない。
    if (!editor || !editor.charCounter || editor.charCounter.count() <= 0) return;
    disabled ?
        editor.edit.off() :
        editor.edit.on();
};


const handlePasteImage = (images: any, ref: RefObject<FroalaEditor>) => {
    const editor = ref.current?.getEditor();
    const base64Image = images.src;
    const sizeInBytes =
        base64Image.length * (3 / 4) -
        (base64Image.includes('=') ? base64Image.split('=')[1].length : 0);

    const mine = base64Image.match(/data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+).*,.*/);
    const mineType = mine && mine.length && mine[1];
    const mineTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp'];

    const showPopupWithMessage = (code: number, message: string) => {
        editor.image.insert('', false, null, editor.image.get());

        const toolbar = editor.$tb;

        editor.popups.setContainer('image.insert', toolbar);
        editor.popups.show('image.insert', toolbar);

        setTimeout(() => {
            editor.events.trigger('image.error', [{ code, message }]);
        }, 10);

        images.remove();
    };

    if (mineTypes.indexOf(mineType) === -1) {
        showPopupWithMessage(ERROR_UPLOAD.BAD_LINK.CODE, ERROR_UPLOAD.BAD_LINK.MESSAGE);
        return false;
    }

    if (sizeInBytes > MAX_SIZE) {
        showPopupWithMessage(ERROR_UPLOAD.TEXT_LARGE.CODE, ERROR_UPLOAD.TEXT_LARGE.MESSAGE);
        return false;
    }
};

const useImage = (ref: RefObject<FroalaEditor>, form: FormInstance, inlineFilesId: string) => {
    const dispatch = useDispatch();
    const fileConnection = useAppSelector(state => state.fileConnection);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const { meta, payload } = fileConnection;

        if (!isLoading || meta.status !== Actions.statusEnum.SUCCESS) return;

        setIsLoading(false);

        const editor = ref.current?.getEditor();
        // https://wysiwyg-editor.froala.help/hc/en-us/articles/115000593909-How-can-I-use-a-different-image-response

        if (!editor) return;

        const fileDomain = generateFileDomain();
        const domain = fileDomain.endsWith('/control') ? fileDomain : `${fileDomain}/admin`;

        const url = `${domain}/file/view/${payload.result.uuid}`;

        // https://froala.com/wysiwyg-editor/docs/methods/#image.insert
        editor.image.insert(
            url,
            false,
            null,
            editor.image.get(),
            { ...payload, link: url }
        );

        const inlineFiles = form.getFieldValue(inlineFilesId) ?? [];
        form.setFieldsValue({
            inlineFiles: inlineFiles.concat([payload.result.uuid]),
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileConnection]);

    const beforeUpload = (files: File[]) => {
        const file = files[0];
        const fileReader = new FileReader();
        const editor = ref.current?.getEditor();
        const mineTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp'];
        const extensions = ['jpeg', 'jpg', 'png', 'gif', 'bmp'];
        const ext = file.name ? file.name.substring(file.name.lastIndexOf(".") + 1) : file.type.split('/')[1];

        if (mineTypes.indexOf(file.type) === -1 || extensions.indexOf(ext) === -1 ) {
            editor.events.trigger('image.error', [{
                code: ERROR_UPLOAD.BAD_LINK.CODE,
                message: ERROR_UPLOAD.BAD_LINK.MESSAGE
            }]);
            return false; 
        }

        if (file.size > MAX_SIZE) {
            editor.events.trigger('image.error', [{
                code: ERROR_UPLOAD.TEXT_LARGE.CODE,
                message:ERROR_UPLOAD.TEXT_LARGE.MESSAGE
            }]);
            return false;
        }

        fileReader.onload = () => {
            dispatch(Actions.http.connection.file.upload(
                file.name ?? `inline-image.${file.type.split('/')[1]}`,
                file.type,
                fileReader.result
            ));
            setIsLoading(true);
        };
        fileReader.readAsArrayBuffer(file);
        return false;
    };

    return [beforeUpload] as const;
};

const handleCommandsRedoAndUndo = (ref: RefObject<FroalaEditor>, form: FormInstance, inlineFilesId: string) => {
    const editor = ref.current?.getEditor();
    const objectRedoUndo = editor?.undo_stack[editor?.undo_index - 1]?.html;

    for (let index = 0; index < editor?.doc?.images.length; index++) {
        const uuid = editor.doc?.images[index]?.src.split('/').pop();

        if (objectRedoUndo.indexOf(uuid) !== -1) {
            const inlineFiles = form.getFieldValue(inlineFilesId) ?? [];
            const isExits = inlineFiles?.some((item: string) => item === uuid);

            if (!isExits) {
                form.setFieldsValue({
                    inlineFiles: inlineFiles.concat(uuid),
                });
            }
        }
        
    }
}
